export type TimingTowerColumnId =
	| 'detailsToggle'
	| 'positionArrow'
	| 'position'
	| 'carNumber'
	| 'carLogo'
	| 'teamDriver'
	| 'classPosition'
	| 'lap'
	| 'gap'
	| 'classGap'
	| 'interval'
	| 'lastLap'
	| 'bestLap'
	| 'sector1'
	| 'sector2'
	| 'sector3'
	| 'pits'
	| 'lsp'
	| 'maxSpeed';

export type ColumnAlignment = 'left' | 'center' | 'right';

export interface TimingTowerColumnConfig {
	id: TimingTowerColumnId;
	label?: string;
	headerClass?: string;
	cellClass?: string;
	alignment?: ColumnAlignment;
	visible: boolean;
	conditional?: boolean;
	widthClass?: string;
}

export interface TimingTowerConfig {
	configId: string;
	columns: TimingTowerColumnConfig[];
	enableDrawer?: boolean;
	enableFavoriteDriver?: boolean;
	enablePlaybackSelection?: boolean;
	gapByClass?: boolean;
}

export const TIMING_TOWER_COMPACT_CONFIG: TimingTowerConfig = {
	configId: 'compact',
	columns: [
		{
			id: 'positionArrow',
			headerClass: 'position-arrow-col',
			cellClass: 'position-arrow-col',
			visible: true,
		},
		{
			id: 'position',
			label: 'P',
			alignment: 'left',
			visible: true,
		},
		{
			id: 'carNumber',
			label: '#',
			alignment: 'left',
			visible: true,
		},
		{
			id: 'carLogo',
			label: 'Car',
			alignment: 'left',
			visible: true,
		},
		{
			id: 'teamDriver',
			label: 'Team/Driver',
			alignment: 'left',
			cellClass: 'team-driver-cell',
			visible: true,
		},
		{
			id: 'classPosition',
			label: 'Pic',
			alignment: 'center',
			visible: true,
		},
		{
			id: 'lap',
			label: 'Lap',
			alignment: 'center',
			visible: true,
		},
		{
			id: 'gap',
			label: 'Gap',
			alignment: 'right',
			visible: true,
		},
		{
			id: 'classGap',
			label: 'Class Gap',
			alignment: 'right',
			visible: true,
			conditional: true,
		},
		{
			id: 'interval',
			label: 'Interval',
			alignment: 'right',
			visible: true,
			conditional: true,
		},
		{
			id: 'lastLap',
			label: 'Last Lap',
			alignment: 'center',
			visible: true,
		},
		{
			id: 'bestLap',
			label: 'Best Lap',
			alignment: 'center',
			visible: true,
			conditional: true,
		},
		{
			id: 'sector1',
			label: 'S1',
			alignment: 'right',
			visible: true,
			conditional: true,
		},
		{
			id: 'sector2',
			label: 'S2',
			alignment: 'right',
			visible: true,
			conditional: true,
		},
		{
			id: 'sector3',
			label: 'S3',
			alignment: 'right',
			visible: true,
			conditional: true,
		},
		{
			id: 'pits',
			label: 'Pits',
			alignment: 'right',
			visible: false,
		},
		{
			id: 'lsp',
			label: 'LSP',
			alignment: 'right',
			visible: false,
		},
		{
			id: 'maxSpeed',
			label: 'Max Speed',
			alignment: 'right',
			visible: false,
		},
	],
	enableDrawer: false,
	enableFavoriteDriver: true,
	enablePlaybackSelection: true,
	gapByClass: false,
};

/**
 * configuration for full timing tower
 */
export const TIMING_TOWER_FULL_CONFIG: TimingTowerConfig = {
	configId: 'full',
	columns: [
		{
			id: 'detailsToggle',
			headerClass: 'details-toggle-header',
			cellClass: 'details-toggle-cell',
			visible: true,
		},
		{
			id: 'positionArrow',
			headerClass: 'position-arrow-col',
			cellClass: 'position-arrow-col',
			visible: true,
		},
		{
			id: 'position',
			label: 'P',
			alignment: 'left',
			cellClass: 'car-info',
			visible: true,
		},
		{
			id: 'carNumber',
			label: '#',
			alignment: 'center',
			cellClass: 'car-info',
			visible: true,
		},
		{
			id: 'carLogo',
			label: 'Car',
			cellClass: 'car-info',
			visible: true,
		},
		{
			id: 'teamDriver',
			label: 'Team/Driver',
			widthClass: 'team-driver-col',
			cellClass: 'team-driver-cell',
			visible: true,
		},
		{
			id: 'classPosition',
			label: 'Pic',
			alignment: 'center',
			cellClass: 'lap-data',
			visible: true,
		},
		{
			id: 'lap',
			label: 'Lap',
			alignment: 'center',
			cellClass: 'lap-data',
			visible: true,
		},
		{
			id: 'gap',
			label: 'Gap',
			alignment: 'right',
			visible: true,
		},
		{
			id: 'classGap',
			label: 'Class Gap',
			alignment: 'right',
			visible: true,
			conditional: true,
		},
		{
			id: 'interval',
			label: 'Interval',
			alignment: 'right',
			visible: true,
		},
		{
			id: 'lastLap',
			label: 'Last Lap',
			alignment: 'center',
			visible: true,
		},
		{
			id: 'bestLap',
			label: 'Best Lap',
			alignment: 'center',
			visible: true,
		},
		{
			id: 'sector1',
			label: 'S1',
			alignment: 'right',
			visible: true,
		},
		{
			id: 'sector2',
			label: 'S2',
			alignment: 'right',
			visible: true,
		},
		{
			id: 'sector3',
			label: 'S3',
			alignment: 'right',
			visible: true,
		},
		{
			id: 'pits',
			label: 'Pits',
			alignment: 'right',
			cellClass: 'pit-col',
			visible: true,
		},
		{
			id: 'lsp',
			label: 'LSP',
			alignment: 'right',
			visible: true,
		},
		{
			id: 'maxSpeed',
			label: 'Max Speed',
			alignment: 'right',
			cellClass: 'text-speed',
			visible: true,
		},
	],
	enableDrawer: true,
	enableFavoriteDriver: false,
	enablePlaybackSelection: false,
	gapByClass: false,
};
